import { ShieldCheckIcon, TrashIcon } from '@heroicons/react/outline';
import { useState } from 'react';
import { Button } from 'components/Button';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { SizeEnum } from 'utils';
import { getReturnCurrencyDigits } from 'utils/returnMoney';
import {
  ShippingCostConfig,
  ShippingCostResponsibility,
} from 'types/incentive';

type ReturnLabelSettingsProps = {
  protectedOrders: ShippingCostConfig;
  unprotectedOrders: ShippingCostConfig;
  onProtectedOrdersChange: (config: ShippingCostConfig) => void;
  onUnprotectedOrdersChange: (config: ShippingCostConfig) => void;
};

const ShippingCostRow = ({
  label,
  description,
  ariaLabel,
  config,
  onChange,
  isProtected = false,
}: {
  label: string;
  description: string;
  ariaLabel: string;
  config: ShippingCostConfig;
  onChange: (config: ShippingCostConfig) => void;
  isProtected?: boolean;
}) => {
  const [currencyCode, setCurrencyCode] = useState('');
  const normalizedCurrency = currencyCode.trim().toUpperCase();
  const currencyFees = config.flatFeeAmountsByCurrency || {};
  const canAddCurrency =
    /^[A-Z]{3}$/.test(normalizedCurrency) &&
    !Object.keys(currencyFees).some(
      (code) => code.trim().toUpperCase() === normalizedCurrency,
    );
  const handleResponsibilityChange = (
    responsibility: ShippingCostResponsibility,
  ) => {
    onChange({
      ...config,
      responsibility,
      ...(responsibility ===
        ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE && {
        flatFeeAmount: config.flatFeeAmount ?? 5,
      }),
    });
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(220px,260px)_150px] lg:items-center">
        <div>
          <div className="flex items-center gap-1.5">
            {isProtected && (
              <ShieldCheckIcon className="h-4 w-4 text-gray-500" />
            )}
            <p className="text-sm font-medium text-gray-900">{label}</p>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        </div>
        <select
          aria-label={`${ariaLabel} return label charge`}
          value={config.responsibility}
          onChange={(event) =>
            handleResponsibilityChange(
              event.target.value as ShippingCostResponsibility,
            )
          }
          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-black focus:ring-black"
        >
          <option value={ShippingCostResponsibility.MERCHANT_PAYS}>
            Free return - merchant pays
          </option>
          <option value={ShippingCostResponsibility.CUSTOMER_PAYS_FULL}>
            Deduct carrier label cost
          </option>
          <option value={ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE}>
            Deduct a flat label fee
          </option>
        </select>
        {config.responsibility ===
        ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE ? (
          <label className="block text-xs text-gray-500">
            Default fee
            <NonNegativeNumberInput
              aria-label={`${ariaLabel} flat return label fee`}
              min="0"
              step="0.01"
              value={config.flatFeeAmount ?? 0}
              onChange={(event) =>
                onChange({
                  ...config,
                  responsibility:
                    ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE,
                  flatFeeAmount: Number(event.target.value),
                })
              }
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-black focus:ring-black"
            />
          </label>
        ) : (
          <div className="hidden lg:block" />
        )}
      </div>
      {config.responsibility ===
        ShippingCostResponsibility.CUSTOMER_PAYS_FLAT_FEE && (
        <div className="mt-3 space-y-3 border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-500">
            Set fees in each order currency. The default applies when no
            currency fee is configured; amounts are not converted.
          </p>
          {Object.entries(currencyFees).map(([code, amount]) => (
            <div key={code} className="flex items-center gap-3">
              <span className="w-12 text-sm font-medium text-gray-700">
                {code}
              </span>
              <NonNegativeNumberInput
                aria-label={`${ariaLabel} ${code} flat return label fee`}
                min="0"
                step={String(10 ** -getReturnCurrencyDigits(code))}
                value={amount}
                onChange={(event) =>
                  onChange({
                    ...config,
                    flatFeeAmountsByCurrency: {
                      ...currencyFees,
                      [code]: Number(event.target.value),
                    },
                  })
                }
                className="block w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-black focus:ring-black"
              />
              <Button
                size={SizeEnum.xs}
                color="none"
                variant="outlined"
                isIconOnly
                props={{
                  'aria-label': `Remove ${ariaLabel} ${code} currency fee`,
                }}
                onClick={() => {
                  const nextFees = { ...currencyFees };
                  delete nextFees[code];
                  onChange({ ...config, flatFeeAmountsByCurrency: nextFees });
                }}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-xs text-gray-500">
              Currency code
              <input
                aria-label={`${ariaLabel} currency code`}
                list={`${ariaLabel.replace(/\s+/g, '-')}-currency-codes`}
                value={currencyCode}
                maxLength={7}
                placeholder="e.g. CAD"
                onChange={(event) => setCurrencyCode(event.target.value)}
                className="mt-1 block w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase text-gray-900 focus:border-black focus:ring-black"
              />
              <datalist id={`${ariaLabel.replace(/\s+/g, '-')}-currency-codes`}>
                {[
                  'AUD',
                  'CAD',
                  'CHF',
                  'EUR',
                  'GBP',
                  'HKD',
                  'JPY',
                  'NZD',
                  'SGD',
                  'USD',
                ].map((code) => (
                  <option key={code} value={code} />
                ))}
              </datalist>
            </label>
            <Button
              size={SizeEnum.sm}
              color="none"
              variant="outlined"
              disabled={!canAddCurrency}
              props={{ 'aria-label': `Add ${ariaLabel} currency fee` }}
              onClick={() => {
                if (!canAddCurrency) return;
                onChange({
                  ...config,
                  flatFeeAmountsByCurrency: {
                    ...currencyFees,
                    [normalizedCurrency]: config.flatFeeAmount ?? 0,
                  },
                });
                setCurrencyCode('');
              }}
            >
              Add currency fee
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Choose or enter a three-letter currency code.
          </p>
        </div>
      )}
    </div>
  );
};

export default function ReturnLabelSettings({
  protectedOrders,
  unprotectedOrders,
  onProtectedOrdersChange,
  onUnprotectedOrdersChange,
}: ReturnLabelSettingsProps) {
  return (
    <section
      id="return-label-charge"
      className="scroll-mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <div className="border-b border-gray-100 px-6 py-4">
        <h3 className="text-base font-semibold text-gray-900">
          Return label settings
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Choose who pays carrier postage. The same fee applies to refunds and
          store credit.
        </p>
      </div>
      <div className="space-y-3 p-5">
        <ShippingCostRow
          label="Standard orders"
          description="No Delivery Guarantee."
          ariaLabel="Standard orders"
          config={unprotectedOrders}
          onChange={onUnprotectedOrdersChange}
        />
        <ShippingCostRow
          label="Guaranteed orders"
          description="Includes Delivery Guarantee."
          ariaLabel="Guaranteed orders"
          config={protectedOrders}
          onChange={onProtectedOrdersChange}
          isProtected
        />
      </div>
    </section>
  );
}
